/*
 *
 * AdminPage / Devices / DeviceDetail
 *
 * Read-only per-device telemetry. Backed by GET /api/v3/admin/device-statuses/:id
 * → DEVICE_STATUSES_DEFAULT_RESPONSE_SHAPE (verified against
 * Api::V3::Admin::DeviceStatusesController#show). Renders the full telemetry set
 * the API returns — battery / iOS / network / signal / memory / queue + upload
 * counts, plus admin + capture metadata.
 */

import React from "react";
import { useParams, useNavigate } from "react-router-dom";

import Loader from "components/Loader";
import { useAdminDeviceStatus } from "../api/queries";
import { networkLabel } from "./network";
import {
  Wrapper,
  BackLink,
  FieldGrid,
  Field,
  DetailLabel,
  FieldValue,
} from "./styledComponents";

const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));
const fmtBattery = (level) =>
  level == null ? "—" : `${Math.round(Number(level) * 100)}%`;

// The DEVICE_STATUSES_DEFAULT_RESPONSE_SHAPE fields, in display order, each with
// its formatter. Keeping this as data makes the rendered set easy to audit
// against the API shape.
const FIELDS = [
  { label: "Device name", get: (d) => fmt(d.device_name) },
  { label: "Device type", get: (d) => fmt(d.device_type) },
  { label: "iOS version", get: (d) => fmt(d.ios_version) },
  { label: "App version", get: (d) => fmt(d.buddybuild_version) },
  { label: "Battery", get: (d) => fmtBattery(d.battery_level) },
  { label: "Network", get: (d) => networkLabel(d.network_type) },
  { label: "Signal strength", get: (d) => fmt(d.signal_strength) },
  { label: "Upload speed", get: (d) => fmt(d.upload_speed) },
  { label: "Memory free", get: (d) => fmt(d.memory_free) },
  { label: "Screen size", get: (d) => fmt(d.screen_size) },
  { label: "GIFs in progress", get: (d) => fmt(d.gifsBeingMadeInProgress) },
  { label: "GIFs remaining", get: (d) => fmt(d.gifsRemaining) },
  { label: "MP4 uploads in progress", get: (d) => fmt(d.mp4UploadsInProgress) },
  { label: "MP4 uploads remaining", get: (d) => fmt(d.mp4UploadsRemaining) },
  { label: "Channel ID", get: (d) => fmt(d.channel_id) },
  { label: "Admin email", get: (d) => fmt(d.admin_email) },
  { label: "IP address", get: (d) => fmt(d.ip_address) },
  { label: "Timezone", get: (d) => fmt(d.timezone) },
  { label: "Last captured", get: (d) => fmt(d.time_captured) },
];

const DeviceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: device, isLoading, isError } = useAdminDeviceStatus(id);

  if (isLoading) {
    return (
      <Wrapper>
        <div data-testid="device-detail-loading">
          <Loader />
        </div>
      </Wrapper>
    );
  }

  if (isError || !device) {
    return (
      <Wrapper>
        <BackLink type="button" onClick={() => navigate("/admin/devices")}>
          ← Back to devices
        </BackLink>
        <p className="admin-devices__error">Couldn’t load this device.</p>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <BackLink type="button" onClick={() => navigate("/admin/devices")}>
        ← Back to devices
      </BackLink>
      <h1>{device.device_name || `Device #${device.id}`}</h1>

      <FieldGrid>
        {FIELDS.map((f) => (
          <Field key={f.label}>
            <DetailLabel>{f.label}</DetailLabel>
            <FieldValue>{f.get(device)}</FieldValue>
          </Field>
        ))}
      </FieldGrid>
    </Wrapper>
  );
};

export default DeviceDetail;
